import React from "react";
import { Snackbar, Alert, Slide, Box } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoIcon from "@mui/icons-material/Info";
import { useTheme, alpha, keyframes } from "@mui/material/styles";

// ─── Animations ────────────────────────────────────────────────────────────

const SlideTransition = (props) => <Slide {...props} direction="down" />;

// Subtle bounce/pop animation for the icons
const popIn = keyframes`
  0% { transform: scale(0.5); opacity: 0; }
  60% { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

const iconMap = {
  success: <CheckCircleIcon fontSize="inherit" />,
  error: <ErrorIcon fontSize="inherit" />,
  warning: <WarningAmberIcon fontSize="inherit" />,
  info: <InfoIcon fontSize="inherit" />,
};

// ─── Component ─────────────────────────────────────────────────────────────

const CustomSnackbar = ({
  open,
  message,
  severity = "info",
  onClose,
  autoHideDuration = 4000,
  sx = {},
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

// Safe severity fallback
const safeSeverity =
  ["success", "error", "warning", "info"].includes(severity)
    ? severity
    : "info";

const severityColor =
  theme.palette[safeSeverity]?.main || theme.palette.info.main;

  return (
    <Snackbar
      open={open}
      onClose={onClose}
      autoHideDuration={autoHideDuration}
      TransitionComponent={SlideTransition}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      sx={{
        mt: 1, // Slight margin from the absolute top
      }}
    >
      <Alert
        icon={iconMap[safeSeverity]}
        onClose={onClose}
        severity={severity}
        variant="standard" // We override styles to make it look premium
        sx={{
          // ── Structural styling ──
          alignItems: "center",
          minWidth: 300,
          maxWidth: 500,
          py: 0.8,
          px: 2,
          borderRadius: "12px",

          // ── Glassmorphism & Colors ──
          backgroundColor: isDark
            ? alpha(theme.palette.background.paper, 0.85)
            : alpha("#ffffff", 0.9),
          backdropFilter: "blur(12px)",
          color: theme.palette.text.primary,
          
          // ── Borders & Elevation ──
          // border: `1px solid ${alpha(theme.palette[severity].main, 0.3)}`,
          border: `1px solid ${alpha(severityColor, 0.3)}`,
          boxShadow: isDark
            ? `0 12px 40px ${alpha("#000", 0.5)}`
            : `0 12px 40px ${alpha(theme.palette.common.black, 0.08)}`,

          // ── Typography ──
          "& .MuiAlert-message": {
            padding: 0,
            ml: 1,
            fontWeight: 600,
            fontSize: "0.85rem",
            letterSpacing: "0.01em",
            fontFamily: "'Plus Jakarta Sans', sans-serif", // Inherit your app's font
          },

          // ── Icon Animation & Styling ──
          "& .MuiAlert-icon": {
            fontSize: "1.4rem",
            opacity: 0, // Starts invisible for the animation
            // color: theme.palette[severity].main,
            color: severityColor,
            animation: `${popIn} 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards`,
            animationDelay: "0.1s", // Slight delay so slide starts first
            alignItems: "center",
            padding: 0,
          },

          // ── Close Button Alignment ──
          "& .MuiAlert-action": {
            paddingTop: 0,
            alignItems: "center",
            mr: -0.5,
          },

          ...sx,
        }}
      >
        {message}
      </Alert>
    </Snackbar>
  );
};

export default CustomSnackbar;

